/**
 * The `anonId` query parameter.
 *
 * A user can arrive carrying an `anonId` on the URL that represents their
 * anonymous identity on the originating surface. We use it to
 * instantiate the Statsig client with that id so feature-gate and experiment
 * bucketing line up across surfaces.
 */
export const ANON_ID_QUERY_PARAM = 'anonId';

/**
 * Reads and trims the `anonId` query parameter from a URL search string.
 *
 *
 * @param search - A URL query string (e.g. `window.location.search`). A leading
 * `?` is optional.
 * @returns The trimmed `anonId` value, or `null` when it is absent, empty, or
 * cannot be parsed.
 */
export const getAnonIdFromQuery = (search: unknown): string | null => {
  if (typeof search !== 'string' || search.length === 0) {
    return null;
  }

  try {
    const value = new URLSearchParams(search).get(ANON_ID_QUERY_PARAM);
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  } catch {
    // `URLSearchParams` is very tolerant, but never let a
    // parsing failure break initialisation for every user.
    return null;
  }
};

/**
 * sessionStorage key used to remember the `anonId` override for the lifetime
 * of the browser tab.
 */
export const ANON_ID_STORAGE_KEY = 'aid.anonIdOverride';

const readStoredAnonId = (): string | null => {
  try {
    const value = window.sessionStorage.getItem(ANON_ID_STORAGE_KEY)?.trim();
    return value ? value : null;
  } catch {
    // Storage can be unavailable (e.g. disabled cookies / privacy mode).
    return null;
  }
};

const storeAnonId = (anonId: string) => {
  try {
    window.sessionStorage.setItem(ANON_ID_STORAGE_KEY, anonId);
  } catch {
    // Best effort only — fall back to query-param-only behaviour.
  }
};

/**
 * Resolves the anonymous identity override used to initialise Statsig.
 *
 * The `anonId` query parameter is usually only present on the first page of a
 * flow: server-built redirects (e.g. signup → OTP → Welcome) rebuild the URL
 * from a fixed set of params and drop it. If we only read the URL, Statsig
 * silently falls back to the analytics anonymous id mid-flow, which re-buckets
 * the user into a potentially different experiment group.
 *
 * To keep bucketing stable, the override is persisted to sessionStorage when
 * first seen and reused on later pages in the same tab. A value present on the
 * URL always wins (and replaces the stored one).
 *
 * @param search - A URL query string (e.g. `window.location.search`).
 * @returns The anonId override, or `null` when none is available.
 */
export const resolveAnonIdOverride = (search: unknown): string | null => {
  const fromQuery = getAnonIdFromQuery(search);
  if (fromQuery) {
    storeAnonId(fromQuery);
    return fromQuery;
  }

  return readStoredAnonId();
};
